import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {DragulaService} from 'ng2-dragula';
import {Subject, Subscription} from 'rxjs';
import {DesignService} from '@services/left-menu/design/design.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {NotificationService} from '@services/notification/notification.service';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {
  UpdateItemsInsideNodeService
} from '@services/left-menu/update-items-inside-node/update-items-inside-node.service';
import {UtilsService} from '@services/utils/utils.service';
import {PageItem} from '@models/sidebar/page-item';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {PageTypeOption} from '@models/sidebar/response/page-type-option';
import {UpdateItemsInsideNodeMessage} from '@models/sidebar/update-items-inside-node-message';
import {User} from '@models/user/user';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {takeUntil} from 'rxjs/operators';
import {USER_ROLE} from '@constants/user-role.constant';
import {ConfirmationDialogComponent} from '@shared/confirmation-dialog/confirmation-dialog.component';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ModalService} from '@services/modal/modal.service';

@Component({
    selector: 'scitis-page-item',
    templateUrl: './page-item.component.html',
    styleUrls: ['./page-item.component.scss'],
    standalone: false
})
export class PageItemComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  baseMenuItems: PageItem[] = [];

  @Input()
  userMenuItems: PageItem[] = [];

  @Input()
  pageItem: PageItem | undefined = undefined;

  @Input()
  isBaseMenuItem: boolean = false;

  @Input()
  pageTypeOptions: PageTypeOption[] = [];

  @Input()
  isAdminOrHigher: boolean = false;

  @Input()
  isHigherThanAdmin: boolean = false;

  @Input()
  dragAndDropUserMenuItemsInProgress: boolean = false;

  @Input()
  refreshNodes: string[] = [];

  @Input()
  allPagesInsideNode: PageItem[] = [];

  @Input()
  menuItemType: MenuItemType | string = '';

  @Input()
  pageItemsToBeDeleted: PageItem[] = [];

  @Output()
  deletePageItem: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  editPageItem: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  propertiesFallback: EventEmitter<Object> = new EventEmitter<Object>();

  @Output()
  editNodeItem: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  sharePageItem: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  sharePageItemInsideNode: EventEmitter<Map<string, PageItem>> = new EventEmitter<Map<string, PageItem>>();

  @Output()
  editPageItemInsideNode: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  unsharePageItem: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  updatedItemsInsideNode: EventEmitter<Map<string, PageItem[]>> = new EventEmitter<Map<string, PageItem[]>>();

  @Output()
  updatedItemsInsideNodeAfterDelete: EventEmitter<PageItem[]> = new EventEmitter<PageItem[]>();

  @Output()
  disableSaveButton: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  pageFormIsActive: EventEmitter<PageItem | null> = new EventEmitter<PageItem | null>();

  @Output()
  itemsInsideNode: EventEmitter<Map<string, PageItem[]>> = new EventEmitter<Map<string, PageItem[]>>();

  public editPageName: boolean = false;
  public editNode: boolean = false;
  public nodeUserMenuItems: PageItem[] = [];
  public updatedNodeUserMenuItems: PageItem[] = [];
  public subscription = new Subscription();
  public user: User | undefined;
  public isSuperAdmin = false;

  dragAndDropUserMenuItems = 'USER_MENU_ITEMS';
  private CURRENT_PAGE_NAME = 'MenuOverview';
  private errorGettingMenuItems = '';
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
      private designService: DesignService,
      private translate: TranslateService,
      private modalService: ModalService,
      private leftMenuService: LeftMenuService,
      private notificationService: NotificationService,
      private translation: TranslateService,
      private dragulaService: DragulaService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private updateItemsInsideNodeService: UpdateItemsInsideNodeService,
      private firebaseAuthenticationService: FirebaseAuthenticationService,
      private utilsService: UtilsService) {
    this.setTranslations();
    this.useDragula();
    this.listenIfNodeChildrenNeedsToBeUpdated();
  }

  get isAlteringDisabled(): boolean {
    if (this.pageItem) {
      return !this.pageItem.canBeAltered;
    }
    return true;
  }

  get displayDeleteAndEditButton(): boolean {
    if (this.isHigherThanAdmin) {
      return this.pageItem?.type !== MenuItemType.MACHINE
          && this.pageItem?.type !== MenuItemType.MACHINE_GROUP
          && this.pageItem?.type !== MenuItemType.DATA_VISUALIZATION_PAGE;
    } else if (this.isAdminOrHigher) {
      if (this.menuItemType === MenuItemType.MACHINE_GROUP) {
        return false;
      }
      return this.pageItem?.type !== MenuItemType.MACHINE
          && this.pageItem?.type !== MenuItemType.MACHINE_GROUP
          && this.pageItem?.type !== MenuItemType.DATA_VISUALIZATION_PAGE
          && this.pageItem?.type !== MenuItemType.FRAME_PAGE
          && this.pageItem?.type !== MenuItemType.EXTERNAL_URL
          && this.pageItem?.type !== MenuItemType.BUCKET_BROWSER_PAGE
          && this.pageItem?.type !== MenuItemType.FORM_PAGE;
    }
    return this.pageItem?.type !== MenuItemType.MACHINE
        && this.pageItem?.type !== MenuItemType.MACHINE_GROUP
        && this.pageItem?.type !== MenuItemType.DATA_VISUALIZATION_PAGE;

  }

  get displayShareButton(): boolean {
    return this.isAdminOrHigher && !this.isBaseMenuItem && this.pageItem?.type === MenuItemType.USER_DEFINED_PAGE;
  }

  get displayUnshareButton(): boolean {
    return this.pageItem?.type === MenuItemType.USER_SHARED_PAGE;
  }

  get displayActionMenu(): boolean {
    return this.displayDeleteAndEditButton || this.displayShareButton;
  }

  get isPageOwner(): boolean {
    if (this.pageItem?.type === MenuItemType.USER_DEFINED_PAGE || this.pageItem?.type === MenuItemType.USER_SHARED_PAGE) {
      return !!this.pageItem?.pageId?.includes(String(this.user?.firebaseUid));
    }
    return true;
  }

  get iconClass(): string {
    if (this.pageItem?.type) {
      return this.designService.getIcon(this.pageItem?.type).toString();
    }
    return '';
  }

  get itemTypeCategory(): string {
    if (this.pageItem?.type === MenuItemType.NODE) {
      return 'node';
    }
    return 'page';
  }

  get isNode(): boolean {
    return this.pageItem?.type === MenuItemType.NODE;
  }

  get displayDummyContainerForEmptyNode(): boolean {
    return this.updatedNodeUserMenuItems.length === 0 && this.dragAndDropUserMenuItemsInProgress;
  }

  get doNotDropHereClass(): string {
    return this.designService.getNodeClass(MenuItemType.NODE);
  }

  get secondLevelContainerClass(): string {
    return DesignService.SECOND_LEVEL_CONTAINER;
  }

  get formIsActive(): boolean {
    return <boolean>this.pageItem?.formIsActive;
  }

  get getId(): string {
    if (this.pageItem && this.pageItem.id) {
      return this.pageItem.id;
    }
    return '';
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler() {
    this.dragulaService.destroy(this.dragAndDropUserMenuItems);
  }

  ngOnInit(): void {
    this.firebaseAuthenticationService.getLoggedUserDetails().pipe(takeUntil(this.destroy$)).subscribe((result: User) => {
      this.user = result;
      this.isSuperAdmin = result.role === USER_ROLE.SUPERADMIN;
    });
    if (this.isNode) {
      this.setNodeChildren();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.refreshNodes && changes.refreshNodes.previousValue && changes.refreshNodes.currentValue &&
        this.utilsService.getObjectDeepCopy(changes.refreshNodes.previousValue) !== this.utilsService.getObjectDeepCopy(changes.refreshNodes.currentValue)) {
      const refreshNodeIds: string[] = changes.refreshNodes.currentValue;
      if (this.pageItem && this.pageItem.id !== null && refreshNodeIds.includes(this.pageItem?.id)) {
        this.setNodeChildren();
      }
    }
  }

  onDelete(pageItem: PageItem): void {
    if (pageItem.type === MenuItemType.TEMPLATE_USER_DEFINED_PAGE || pageItem.type === MenuItemType.TEMPLATE_STATUS_EVENT_PAGE) {
      const modalRef = this.openConfirmationDialog();
      modalRef.result.then(() => {
      }, (reason) => {
        if (reason === ModalDialogDismissOption.OK) {
          this.emitDeletionAndSaveButtonTrigger(pageItem);
        }
      });
    } else {
      this.emitDeletionAndSaveButtonTrigger(pageItem);
    }
  }

  editPage(): void {
    if (this.pageItem?.type === MenuItemType.NODE) {
      this.editNode = true;
    } else {
      this.editPageName = true;
    }
    this.pageItem!.formIsActive = true;
    this.pageFormIsActive.emit(this.pageItem);
    this.disableSaveButton.emit(true);
    this.deactivateNodeItemsForm();
  }

  onCancelSavePage(): void {
    this.editPageName = false;
    this.pageFormIsActive.emit(null);
    this.disableSaveButton.emit(false);
  }

  onSavePage(pageItem: PageItem): void {
    this.editPageItem.emit(pageItem);
    this.editPageName = false;
    this.pageFormIsActive.emit(null);
    this.disableSaveButton.emit(false);
  }

  setPropertiesFallback(nameFallback: Object): void {
    this.propertiesFallback.emit(nameFallback);
  }

  onSavePageInsideNode(pageItem: PageItem): void {
    this.editPageItemInsideNode.emit(pageItem);
  }

  onDisableSaveButton(disable: boolean): void {
    this.disableSaveButton.emit(disable);
  }

  onCancelSaveNode(): void {
    this.editNode = false;
    this.disableSaveButton.emit(false);
  }

  onSaveNode(pageItem: PageItem): void {
    this.editNodeItem.emit(pageItem);
    this.editNode = false;
    this.disableSaveButton.emit(false);
  }

  onDeletePageInsideNode(pageItem: PageItem): void {
    this.updatedNodeUserMenuItems = this.updatedNodeUserMenuItems.filter(m => m.id !== pageItem.id);
    this.deletePage(pageItem);
  }

  onDeactivatePageForms(pageItem: PageItem | null): void {
    this.pageFormIsActive.emit(pageItem);
  }

  share(): void {
    this.sharePageItem.emit(this.pageItem);
    this.disableSaveButton.emit(false);
  }

  onSharePageInsideNode(pageItemInsideNode: PageItem): void {
    if (this.pageItem && this.pageItem.id) {
      const map = new Map<string, PageItem>();
      map.set(this.pageItem.id, pageItemInsideNode);
      this.sharePageItemInsideNode.emit(map);
    }
  }

  unshare(): void {
    this.unsharePageItem.emit(this.pageItem);
    this.disableSaveButton.emit(false);
  }

  nodeClass(pageItem: PageItem | undefined): string {
    if (pageItem) {
      return this.designService.getNodeClass(pageItem?.type);
    }
    return '';
  }

  setNodeChildren(): void {
    if (this.pageItem && this.pageItem.id) {
      const pageId: string = this.pageItem.id;
      this.leftMenuService.getUserMenuItems(pageId, false).subscribe(results => {
        this.nodeUserMenuItems = results;
        const map = new Map<string, PageItem[]>();
        map.set(pageId, this.nodeUserMenuItems);
        this.itemsInsideNode.emit(map);
        this.updatedNodeUserMenuItems = results;
        this.updatedNodeUserMenuItems.sort((function (a, b) {
          return a.index - b.index;
        }));
        this.updatedNodeUserMenuItems.forEach(m => m.canBeAltered = true);
      }, error => {
        this.notificationService.error(this.errorGettingMenuItems, error, this.CURRENT_PAGE_NAME, true);
      });
    }
  }

  private setTranslations(): void {
    this.errorGettingMenuItems = this.translation.instant('Error loading menu items.');
  }

  private useDragula() {
    this.subscription.add(this.dragulaService.drag(this.dragAndDropUserMenuItems)
        .subscribe(({el}) => {
          el.classList.remove('ex-moved');
        })
    );
    this.subscription.add(this.dragulaService.drop(this.dragAndDropUserMenuItems)
        .subscribe(({el}) => {
          el.classList.add('ex-moved');
          if (this.pageItem?.id && this.pageItem.type === MenuItemType.NODE) {
            const map = new Map<string, PageItem[]>();
            const key = this.pageItem.id + '_' + this.pageItem.name;
            map.set(key, this.updatedNodeUserMenuItems);
            this.updatedItemsInsideNode.emit(map);
          }
        })
    );
    this.subscription.add(this.dragulaService.over(this.dragAndDropUserMenuItems)
        .subscribe(({container}) => {
          container.classList.add('ex-over');
        })
    );
    this.subscription.add(this.dragulaService.out(this.dragAndDropUserMenuItems)
        .subscribe(({container}) => {
          container.classList.remove('ex-over');
        })
    );
  }

  private listenIfNodeChildrenNeedsToBeUpdated(): void {
    this.subscription.add(this.updateItemsInsideNodeService.currentMessage
        .subscribe((message: UpdateItemsInsideNodeMessage) => {
          if (message.nodeId === this.pageItem?.id) {
            this.setNodeChildren();
          }
        })
    );
  }

  private deletePage(pageItem: PageItem): void {
    this.deletePageItem.emit(pageItem);
  }

  private deactivateNodeItemsForm(): void {
    for (const item of this.updatedNodeUserMenuItems) {
      item.formIsActive = false;
    }
  }

  private openConfirmationDialog(): NgbModalRef {
    const messageTitle = this.translate.instant('Confirm delete');
    const messageBody = this.translate.instant('Deleting this page affects the template page on all machines with the same tag. Do you want to continue?');
    const messageOK = this.translate.instant('Yes');
    const messageCancel = this.translate.instant('No');
    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    modalRef.componentInstance.title = messageTitle;
    modalRef.componentInstance.body = messageBody;
    modalRef.componentInstance.cancelButtonText = messageCancel;
    modalRef.componentInstance.okButtonText = messageOK;

    return modalRef;
  }

  private emitDeletionAndSaveButtonTrigger(pageItem: PageItem) {
    if (this.isNode && this.nodeUserMenuItems.length > 0) {
      this.updatedItemsInsideNodeAfterDelete.emit(this.nodeUserMenuItems);
    }
    this.disableSaveButton.emit(false);
    this.deletePage(pageItem);
  }
}
